/** ***********************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 ************************************************************************* */

import React from 'react';
import { observer } from 'mobx-react';
import { observable, action, computed } from 'mobx';
import { TabList, Tab } from '@react/react-spectrum/TabList';
import IconMapping from '../../../../utils/iconMapping';

class SubSectionTabs extends React.Component {
    @observable

    componentWillMount() {
        const { history, location, appsSkeletonModel } = this.props;
        this.unlisten = history.listen(l => this.updateTabList(l, appsSkeletonModel));
        this.updateTabList(location, appsSkeletonModel);
    }

    componentWillUnmount() {
        this.unlisten();
    }

    onSelect = (subSection) => {
        const { history, match } = this.props;
        const path = `${match.path}/${subSection.id}`;
        if (history.location.pathname !== path) {
            history.push(path);
        }
    }

    @computed
    get selectedItemIndex() {
        return this.visibleSubSections.findIndex(s => s.id === this.selectedItemId);
    }

    @computed
    get visibleSubSections() {
        const { allAppsModel, subSections, appSection } = this.props;
        return subSections.filter(({ id }) => allAppsModel.existApps({
            appSectionFilter: appSection.id,
            subSectionFilter: id,
            isHidden: false,
        }));
    }

    @action
    updateSelectedItemId = (id, appsSkeletonModel) => {
        this.selectedItemId = id;
        appsSkeletonModel.setSelectedSubSection(id);
    }

    updateTabList = (location, appsSkeletonModel) => {
        const { history, match } = this.props;
        const { visibleSubSections, updateSelectedItemId } = this;
        const defaultSection = visibleSubSections.find(s => s.isDefault) || visibleSubSections[0];
        if (defaultSection && match.path === location.pathname) {
            history.replace(`${match.path}/${defaultSection.id}${location.hash ? location.hash : ''}`);
            return;
        }
        visibleSubSections.forEach((subSection) => {
            if (location.pathname.startsWith(`${match.path}/${subSection.id}`)) {
                updateSelectedItemId(subSection.id, appsSkeletonModel);
            }
        });
    }

    renderSubSection = (subSection) => {
        const {
            id,
            icon,
            localizedText,
            ariaLabel,
        } = subSection;
        const TabIcon = IconMapping[icon];
        return (
            <Tab
                id={`${id}-tab`}
                aria-controls={`${id}-tabpanel`}
                key={id}
                icon={TabIcon && <TabIcon size="S" />}
                aria-label={ariaLabel}
                onFocus={() => this.onSelect(subSection)}
                onClick={() => this.onSelect(subSection)}>
                {localizedText}
            </Tab>
        );
    }

    render() {
        const { subSectionAriaLabel } = this.props;
        const {
            selectedItemIndex,
            visibleSubSections,
            renderSubSection,
        } = this;

        if (!visibleSubSections.length) {
            return [];
        }

        return [
            <TabList
                key={0}
                selectedIndex={selectedItemIndex}
                className="app-platform-tab-list"
                aria-label={subSectionAriaLabel}>
                { visibleSubSections.map(renderSubSection) }
            </TabList>,
        ];
    }
}

export default observer(SubSectionTabs);
